import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { WorkoutProgramParams } from 'src/api/mutations/useWorkoutProgram';

// -----------------------------------------------------------------------------
// State shape
// -----------------------------------------------------------------------------

type ProgramState = {
  /** The full program object returned by the generation API. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  program: any | null;
  /** The payload used to generate (and re-generate) the program. */
  payload: WorkoutProgramParams | null;
  /**
   * The full weeks/exercise state from useProgramStore – including every
   * drag-and-drop change the trainer made.
   * `null` means "not yet set – initialise from apiProgramData".
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  weeks: any[] | null;
};

// -----------------------------------------------------------------------------
// Store
// `persist` writes to localStorage so the program survives a page refresh.
// The store is cleared explicitly in two places:
//   1. When the trainer clicks "Create a Program" (fresh session start).
//   2. After the trainer successfully joins the waitlist on the completed screen.
// -----------------------------------------------------------------------------

export const programStore = create<ProgramState>()(
  persist<ProgramState>(
    () => ({
      program: null,
      payload: null,
      weeks: null,
    }),
    {
      name: 'minipt_program_state',
    },
  ),
);

// -----------------------------------------------------------------------------
// Actions – defined outside the store, following the project's existing pattern
// (see trainingStore.ts)
// -----------------------------------------------------------------------------

/** Store the freshly generated program + payload and reset the weeks cache. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setGeneratedProgram = (program: any, payload: WorkoutProgramParams) =>
  programStore.setState({ program, payload, weeks: null });

/** Update only the program (e.g. after a remix / AI modification). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateGeneratedProgram = (program: any) =>
  programStore.setState((s) => ({ ...s, program }));

/**
 * Persist the current weeks state so back-navigation from /completed and
 * page refreshes both restore every drag-and-drop change the trainer made.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setStoredWeeks = (weeks: any[]) => programStore.setState((s) => ({ ...s, weeks }));

/**
 * Wipe all stored program data.
 * Call this when the trainer starts a brand-new program or completes the waitlist flow.
 */
export const clearGeneratedProgram = () =>
  programStore.setState({ program: null, payload: null, weeks: null });
